import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';
import { format } from 'date-fns';

const { 
  FiCalendar, 
  FiUsers, 
  FiClock, 
  FiCheck, 
  FiX, 
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiSearch
} = FiIcons;

const WeeklyAttendance = ({ onNavigate }) => {
  const { 
    members, 
    getTodaysMeeting, 
    checkInMember, 
    isTuesday, 
    isMeetingTime, 
    isTodayMeetingCancelled,
    cancelTodaysMeeting,
    uncancelTodaysMeeting,
    endMeeting
  } = useAttendance();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [checkinSuccess, setCheckinSuccess] = useState(false);
  const [checkinError, setCheckinError] = useState('');
  const [showEndMeetingConfirm, setShowEndMeetingConfirm] = useState(false);
  
  const todaysMeeting = getTodaysMeeting();
  const isActive = todaysMeeting && todaysMeeting.isActive;
  
  // Reset search when meeting changes
  useEffect(() => {
    setSearchTerm('');
    setSelectedMember(null);
    setCheckinSuccess(false);
    setCheckinError('');
  }, [todaysMeeting?.id]);
  
  const filteredMembers = members
    .filter(m => m.active)
    .filter(m => 
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (m.company && m.company.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedMember(null);
  };
  
  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setCheckinSuccess(false);
    setCheckinError('');
  };
  
  const handleCheckIn = async () => {
    if (!selectedMember) return;
    
    try {
      await checkInMember(selectedMember.id);
      setCheckinSuccess(true);
      setCheckinError('');
      setSelectedMember(null);
      setSearchTerm('');
    } catch (error) {
      setCheckinError(error.message || 'Failed to check in');
      setCheckinSuccess(false);
    }
  };
  
  const handleEndMeeting = () => {
    setShowEndMeetingConfirm(true);
  };
  
  const confirmEndMeeting = async () => {
    await endMeeting();
    setShowEndMeetingConfirm(false);
  };
  
  const getMeetingStatus = () => {
    if (!isTuesday()) {
      return {
        status: 'No Meeting Today',
        message: 'Meetings are held on Tuesdays only',
        color: 'bg-gray-100 text-gray-800'
      };
    }
    
    if (isTodayMeetingCancelled()) {
      return {
        status: 'Meeting Cancelled',
        message: 'Today\'s meeting has been cancelled',
        color: 'bg-red-100 text-red-800'
      };
    }
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinutes;
    
    if (currentTime < 7 * 60) { // Before 7:00 AM
      return {
        status: 'upcoming',
        message: 'Meeting starts at 8:00 AM',
        color: 'bg-blue-100 text-blue-800'
      };
    } else if (currentTime < 8 * 60 + 5) { // Before 8:05 AM
      return {
        status: 'checkin',
        message: 'Check-in period active',
        color: 'bg-green-100 text-green-800'
      };
    } else if (currentTime < 10 * 60) { // Before 10:00 AM
      return {
        status: 'meeting',
        message: 'Meeting in progress (late check-ins)',
        color: 'bg-orange-100 text-orange-800'
      };
    } else if (currentTime < 10 * 60 + 30) { // 10:00 AM - 10:30 AM
      return {
        status: 'ending',
        message: 'Meeting ended - processing attendance',
        color: 'bg-red-100 text-red-800'
      };
    } else {
      return {
        status: 'ended',
        message: 'Meeting completed',
        color: 'bg-gray-100 text-gray-800'
      };
    }
  };
  
  const meetingStatus = getMeetingStatus();
  
  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-gray-800">Meeting Control</h2>
        <p className="text-gray-600 mt-2">
          Manage check-ins for today's meeting
        </p>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiCalendar} className="mr-3" />
            Meeting Status
          </h3>
        </div>
        <div className="card-body">
          <div className={`p-4 rounded-lg ${meetingStatus.color}`}>
            <h4 className="font-semibold text-lg mb-2">
              {isTodayMeetingCancelled() ? 'Meeting Cancelled' : isActive ? 'Meeting Active' : 'No Active Meeting'}
            </h4>
            <p>{meetingStatus.message}</p>
          </div>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-2">Meeting Information</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Date:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Meeting Time:</span>
                  <span className="font-semibold text-gray-900">
                    8:00 AM - 9:00 AM (Late after 8:05 AM)
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${isTodayMeetingCancelled() ? 'bg-red-100 text-red-800' : isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {isTodayMeetingCancelled() ? 'CANCELLED' : isActive ? 'ACTIVE' : 'NOT STARTED'}
                  </span>
                </div>
                
                {isActive && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Check-ins:</span>
                    <span className="font-semibold text-green-600">
                      {todaysMeeting?.attendees?.length || 0} members
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 flex flex-col justify-center">
              <h4 className="font-semibold text-blue-800 mb-2">Meeting Controls</h4>
              
              {isTodayMeetingCancelled() ? (
                <button
                  onClick={uncancelTodaysMeeting}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 w-full flex items-center justify-center"
                >
                  <SafeIcon icon={FiCheck} className="mr-2" />
                  Uncancel Meeting
                </button>
              ) : isActive ? (
                <button
                  onClick={handleEndMeeting}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 w-full flex items-center justify-center"
                >
                  <SafeIcon icon={FiX} className="mr-2" />
                  End Meeting & Process Absences
                </button>
              ) : isTuesday() ? (
                <div className="space-y-3">
                  <button
                    onClick={() => checkInMember(null, new Date())}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 w-full flex items-center justify-center"
                  >
                    <SafeIcon icon={FiCalendar} className="mr-2" />
                    Start Today's Meeting
                  </button>
                  
                  <button
                    onClick={cancelTodaysMeeting}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 w-full flex items-center justify-center"
                  >
                    <SafeIcon icon={FiX} className="mr-2" />
                    Cancel Today's Meeting
                  </button>
                </div>
              ) : (
                <p className="text-blue-700 text-center">
                  No meeting controls available. Meetings are only held on Tuesdays.
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      
      {isActive && !isTodayMeetingCancelled() && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiUsers} className="mr-3" />
              Member Check-In
            </h3>
          </div>
          <div className="card-body">
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <SafeIcon icon={FiSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  placeholder="Search members by name or company..."
                />
              </div>
            </div>
            
            {checkinSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-3">
                    <SafeIcon icon={FiCheckCircle} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800">Check-in Successful!</h4>
                    <p className="text-green-700">The member has been checked in successfully.</p>
                  </div>
                </div>
              </div>
            )}
            
            {checkinError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-3">
                    <SafeIcon icon={FiXCircle} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-800">Check-in Failed</h4>
                    <p className="text-red-700">{checkinError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Member List</h4>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 max-h-96 overflow-y-auto">
                  {filteredMembers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <SafeIcon icon={FiUsers} className="mx-auto text-4xl mb-4" />
                      <p>No members found</p>
                      <p className="text-sm">Try adjusting your search</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredMembers.map(member => {
                        const isCheckedIn = todaysMeeting?.attendees?.some(a => a.memberId === member.id);
                        return (
                          <div
                            key={member.id}
                            className={`p-3 rounded-lg flex items-center cursor-pointer transition-colors ${
                              selectedMember?.id === member.id
                                ? 'bg-blue-100 border border-blue-300'
                                : isCheckedIn
                                  ? 'bg-green-50 border border-green-200'
                                  : 'bg-white border border-gray-200 hover:bg-gray-50'
                            }`}
                            onClick={() => !isCheckedIn && handleSelectMember(member)}
                          >
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{member.name}</p>
                              {member.company && (
                                <p className="text-xs text-gray-600">{member.company}</p>
                              )}
                            </div>
                            {isCheckedIn && (
                              <div className="ml-3">
                                <div className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium flex items-center">
                                  <SafeIcon icon={FiCheck} className="mr-1" />
                                  Checked In
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Check-In Form</h4>
                <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 h-full flex flex-col">
                  {selectedMember ? (
                    <>
                      <div className="mb-6">
                        <h4 className="font-semibold text-xl text-gray-900 mb-2">{selectedMember.name}</h4>
                        {selectedMember.company && (
                          <p className="text-gray-600">{selectedMember.company}</p>
                        )}
                        <div className="mt-4">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <SafeIcon icon={FiClock} />
                            <span>
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {new Date().getHours() >= 8 && new Date().getMinutes() >= 5 ? ' (Late)' : ' (On Time)'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={handleCheckIn}
                        className="mt-auto bg-brand-primary hover:bg-brand-secondary text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200"
                      >
                        Check In Member
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <SafeIcon icon={FiUsers} className="text-5xl text-blue-400 mb-4" />
                      <p className="text-blue-800 font-medium mb-2">No Member Selected</p>
                      <p className="text-blue-600 text-sm">
                        Please select a member from the list to check them in.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {todaysMeeting?.attendees?.length > 0 && (
              <div className="mt-8">
                <h4 className="font-medium text-gray-700 mb-3">Today's Attendees ({todaysMeeting.attendees.length})</h4>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {todaysMeeting.attendees.map(attendee => {
                      const member = members.find(m => m.id === attendee.memberId);
                      if (!member) return null;
                      return (
                        <div
                          key={attendee.memberId}
                          className={`p-3 rounded-lg ${
                            attendee.minutesLate > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'
                          }`}
                        >
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-600">
                              {format(new Date(attendee.checkInTime), 'h:mm a')}
                            </span>
                            {attendee.minutesLate > 0 && (
                              <span className="text-xs text-orange-600 font-medium">
                                {attendee.minutesLate} min late
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
      
      <AnimatePresence>
        {showEndMeetingConfirm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
              <div className="text-red-500 text-5xl mb-4 flex justify-center">
                <SafeIcon icon={FiAlertCircle} />
              </div>
              <h3 className="text-xl font-bold text-red-600 mb-4 text-center">End Meeting</h3>
              <p className="text-gray-700 mb-4">
                Are you sure you want to end today's meeting? This will:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
                <li>Mark the meeting as completed</li>
                <li>Record absences for all non-attendees</li>
                <li>Increment consecutive absence counts</li>
                <li>Generate the absence report</li>
              </ul>
              <div className="flex space-x-3">
                <button
                  onClick={confirmEndMeeting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                >
                  End Meeting
                </button>
                <button
                  onClick={() => setShowEndMeetingConfirm(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WeeklyAttendance;